import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

function clamp(n, min, max) {
  if (!Number.isFinite(n)) return 0
  return Math.min(max, Math.max(min, n))
}

async function loadImage(src) {
  const url = String(src || '').trim()
  if (!url) return null
  return await new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = url
  })
}

export const ImageCropper = forwardRef(function ImageCropper(
  { src, className = '', minZoom = 1, maxZoom = 3, initialZoom = 1 },
  ref,
) {
  const boxRef = useRef(null)
  const [box, setBox] = useState({ w: 0, h: 0 })
  const [natural, setNatural] = useState({ w: 0, h: 0 })
  const [zoom, setZoom] = useState(clamp(Number(initialZoom) || 1, minZoom, maxZoom))
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragRef = useRef({ on: false, sx: 0, sy: 0, ox: 0, oy: 0 })

  useEffect(() => {
    let mounted = true
    loadImage(src).then((img) => {
      if (!mounted) return
      setNatural({ w: img?.naturalWidth || 0, h: img?.naturalHeight || 0 })
      setOffset({ x: 0, y: 0 })
      setZoom(clamp(Number(initialZoom) || 1, minZoom, maxZoom))
    })
    return () => {
      mounted = false
    }
  }, [initialZoom, maxZoom, minZoom, src])

  useEffect(() => {
    const el = boxRef.current
    if (!el) return

    const apply = () => {
      const r = el.getBoundingClientRect()
      setBox({ w: Math.round(r.width), h: Math.round(r.height) })
    }

    apply()
    const ro = new ResizeObserver(() => apply())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const baseScale = useMemo(() => {
    const iw = natural.w
    const ih = natural.h
    if (!iw || !ih || !box.w || !box.h) return 1
    return Math.max(box.w / iw, box.h / ih)
  }, [box.h, box.w, natural.h, natural.w])

  const display = useMemo(() => {
    const scale = baseScale * zoom
    const dw = natural.w * scale
    const dh = natural.h * scale
    const maxX = Math.max(0, (dw - box.w) / 2)
    const maxY = Math.max(0, (dh - box.h) / 2)
    return { scale, dw, dh, maxX, maxY }
  }, [baseScale, box.h, box.w, natural.h, natural.w, zoom])

  const clampedOffset = useMemo(() => {
    return {
      x: clamp(offset.x, -display.maxX, display.maxX),
      y: clamp(offset.y, -display.maxY, display.maxY),
    }
  }, [display.maxX, display.maxY, offset.x, offset.y])

  useEffect(() => {
    if (clampedOffset.x !== offset.x || clampedOffset.y !== offset.y) setOffset(clampedOffset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clampedOffset.x, clampedOffset.y])

  const onPointerDown = (e) => {
    if (!boxRef.current) return
    dragRef.current = { on: true, sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {}
  }

  const onPointerMove = (e) => {
    if (!dragRef.current.on) return
    const dx = e.clientX - dragRef.current.sx
    const dy = e.clientY - dragRef.current.sy
    setOffset({
      x: clamp(dragRef.current.ox + dx, -display.maxX, display.maxX),
      y: clamp(dragRef.current.oy + dy, -display.maxY, display.maxY),
    })
  }

  const onPointerUp = () => {
    dragRef.current.on = false
  }

  useImperativeHandle(
    ref,
    () => ({
      async exportBlob({ maxSize = 1080, quality = 0.9, mime = 'image/jpeg' } = {}) {
        const img = await loadImage(src)
        if (!img || !img.naturalWidth || !img.naturalHeight || !box.w || !box.h) return null

        const scale = baseScale * zoom
        const dw = img.naturalWidth * scale
        const dh = img.naturalHeight * scale
        const cx = box.w / 2 + clampedOffset.x
        const cy = box.h / 2 + clampedOffset.y
        const left = cx - dw / 2
        const top = cy - dh / 2

        const cropX = clamp((0 - left) / scale, 0, img.naturalWidth)
        const cropY = clamp((0 - top) / scale, 0, img.naturalHeight)
        const cropW = clamp(box.w / scale, 1, img.naturalWidth - cropX)
        const cropH = clamp(box.h / scale, 1, img.naturalHeight - cropY)

        const outW = Math.max(64, Math.min(Number(maxSize) || 1080, Math.round(cropW)))
        const outH = Math.max(64, Math.round((outW * cropH) / cropW))

        const canvas = document.createElement('canvas')
        canvas.width = outW
        canvas.height = outH
        const ctx = canvas.getContext('2d')
        if (!ctx) return null

        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, outW, outH)

        return await new Promise((resolve) => {
          canvas.toBlob(
            (blob) => resolve(blob || null),
            mime,
            clamp(Number(quality) || 0.9, 0.4, 0.98),
          )
        })
      },
      reset() {
        setZoom(1)
        setOffset({ x: 0, y: 0 })
      },
    }),
    [baseScale, box.h, box.w, clampedOffset.x, clampedOffset.y, src, zoom],
  )

  const scale = display.scale

  const transform = useMemo(() => {
    const ox = clampedOffset.x
    const oy = clampedOffset.y
    return `translate(-50%, -50%) translate(${ox}px, ${oy}px) scale(${scale})`
  }, [clampedOffset.x, clampedOffset.y, scale])

  return (
    <div className={`space-y-3 ${className}`.trim()}>
      <div
        ref={boxRef}
        className="relative w-full h-72 rounded-3xl overflow-hidden border-2 border-brand-textDark bg-black/5 touch-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {src ? (
          <img
            src={src}
            alt="crop"
            draggable={false}
            className="absolute left-1/2 top-1/2 select-none"
            style={{
              width: `${natural.w || 1}px`,
              height: `${natural.h || 1}px`,
              transform,
              transformOrigin: 'center',
            }}
          />
        ) : null}
        <div className="absolute inset-0 pointer-events-none ring-2 ring-white/70" />
      </div>

      <div className="flex items-center gap-3">
        <div className="text-xs font-black text-brand-text/70 w-12 shrink-0">Zoom</div>
        <input
          type="range"
          min={minZoom}
          max={maxZoom}
          step={0.01}
          value={zoom}
          onChange={(e) => setZoom(clamp(Number(e.target.value), minZoom, maxZoom))}
          className="flex-1 accent-brand-pink"
        />
        <div className="text-xs font-mono text-brand-text/70 w-14 text-right shrink-0">{zoom.toFixed(2)}×</div>
      </div>

      <div className="text-xs font-bold text-brand-text/70">拖动图片 + 缩放即可裁剪（不用按四个角）。</div>
    </div>
  )
})

